import { BACKGROUND_ID_PREFIX } from '@/TokamapEditor/helpers/planBackground';
import type { PlanCategory }    from '@/TokamapEditor/helpers/planLayerMapping';
import type { LatestRawFeature } from '@tokamap/core';
import type { DxfPlacement }     from '@models/map/dxfTypes';
import type { PlanShape }        from '@models/map/dxfTypes';

/**
 * **Aperçu global des formes** de l'étape « calques à importer » : toutes les formes du plan
 * posées sur la carte, pour qu'on voie ce qu'on coche.
 *
 * ## Le problème que ça résout
 *
 * Sur un plan sans structure — raster, PDF scanné, SVG de graphiste —, les lignes de l'écran de
 * mapping s'appellent « Forme 12 » et le bouton œil n'en montre qu'une à la fois, cadrée sur
 * elle-même. L'opérateur coche à l'aveugle et ne s'en aperçoit qu'à la carte produite.
 *
 * ## Le repère : celui du fond, et rien d'autre
 *
 * `POST /plan/shapes` rend ses formes en PIXELS de l'aperçu de `/plan/source` — le même repère
 * que la zone d'analyse, et celui de l'image posée en fond. On applique donc aux points la même
 * affine que {@link planBackground} applique aux 4 coins de cette image : formes et fond sont
 * alignés par construction, sans calage, et le restent si l'opérateur déplace le fond.
 *
 * ## L'aperçu se REGARDE, il ne se modifie pas
 *
 * Les formes sont verrouillées et ne portent aucun geste : ce qui décide de l'import, ce sont les
 * cases de la liste. L'aperçu les reflète (une forme écartée passe en gris) et le bouton
 * œil fait le chemin inverse, en surlignant sur la carte les formes d'une ligne.
 *
 * Le décochage AU CLIC sur la carte a existé puis été retiré : l'éditeur écoute le même
 * évènement pour sa propre sélection, et deux gestes concurrents sur le même clic ne se
 * distinguaient pas à l'usage. L'id d'une forme reste construit sur le couple `calque + rang`,
 * ce qui suffit à la retrouver pour la surligner ou la repeindre.
 */

/**
 * **Une couleur = un état, et rien d'autre.**
 *
 * L'aperçu reprenait jusqu'ici la couleur du plan pour les formes retenues. À l'usage, ça ne
 * dit rien à qui trie : sur un plan bleu, une forme gardée est bleu foncé au trait et bleu
 * clair au remplissage — deux teintes pour UN seul état, à côté d'un ambre et de deux gris qui
 * en portent trois autres. On ne peut pas lire ça.
 *
 * Les quatre couleurs ci-dessous sont donc FIXES et ne doivent rien au document : ce sont les
 * quatre réponses à la seule question de cet écran — qu'est-ce qui sera créé ? La couleur du
 * calque, elle, ne sert plus à distinguer les calques ici ; c'est le rôle de la liste de
 * droite, où chaque catégorie est nommée.
 */

/** Forme qui SERA IMPORTÉE. Le vert est le seul état positif de l'écran. */
const ENABLED_COLOUR = '#52c41a';

/**
 * Forme SIGNALÉE, en attente d'arbitrage.
 *
 * Ni le vert (il dirait « tout va bien ») ni le gris (il dirait « c'est réglé ») : l'ambre est
 * un troisième état, et c'est le seul moyen de distinguer d'un coup d'œil les six formes à
 * regarder des mille qui n'ont rien demandé.
 */
const DOUBTFUL_COLOUR = '#faad14';

/** Forme ÉCARTÉE par l'opérateur : gris PLEIN — décidée, et hors de l'import. */
const DISABLED_COLOUR = '#bfbfbf';

/**
 * Contour que l'import NE SAIT PAS créer — hors de la liste blanche de formes simples.
 *
 * Distinct de l'écarté ordinaire, et pas seulement par sa nuance de gris : il est peint SANS
 * REMPLISSAGE, en pointillé. « Je n'en veux pas » et « je ne sais pas le faire » ne se
 * corrigent pas de la même façon, et jusqu'ici le second ne s'affichait pas du tout. Le contour
 * disparaissait de l'écran comme de l'import, et l'opérateur ne s'en apercevait qu'en comptant
 * ses POI.
 */
const UNSUPPORTED_COLOUR = '#595959';

/**
 * Plafond de formes posées sur la carte.
 *
 * Chaque forme est une forme d'éditeur à part entière : sur un plan de salon dense, tout poser
 * fige la carte au lieu d'aider. Au-delà, l'appelant prévient l'opérateur — la sortie est la zone
 * d'analyse ou un filtre de calques, pas un aperçu inutilisable.
 */
export const MAX_OVERLAY_SHAPES = 1500;

/** Style d'une forme de l'aperçu, selon qu'elle sera importée ou non. */
export type PlanShapeStyle = {
	bgColor: string;
	borderColor: string;
	borderWidth: number;
	borderDashed: boolean;
};

/**
 * Ce que l'aperçu doit DIRE d'une forme, par ordre de priorité d'affichage.
 *
 * Quatre états et pas deux, parce que « ne sera pas importée » recouvrait jusqu'ici trois
 * situations que l'opérateur ne peut pas traiter pareil : je n'en veux pas (`discarded`), je ne
 * sais pas la faire (`unsupported`), et regarde-la avant de décider (`doubtful`).
 */
export type PlanShapeState = 'enabled' | 'doubtful' | 'unsupported' | 'discarded';

/**
 * L'état d'affichage d'une forme, du signal le plus fort au plus faible.
 *
 * L'ordre compte : une forme non reconnue ET signalée se peint en AMBRE, pas en gris. Le doute
 * est ce qu'on demande à l'opérateur de faire ; le motif du doute, il le lira sur la carte.
 */
export function planShapeState(params: {
	shape: PlanShape;
	enabled: boolean;
	doubtful: boolean;
}): PlanShapeState {
	if (params.doubtful) return 'doubtful';
	// `shape` n'est renseigné que par `/plan/analysis` ; `undefined` (réponse de
	// `/plan/shapes`, qui ne rend que l'importable) ne veut pas dire « non reconnue ».
	if (null === params.shape.shape) return 'unsupported';

	return params.enabled ? 'enabled' : 'discarded';
}

/**
 * Id de la forme d'aperçu d'une forme du plan.
 *
 * Porte le préfixe commun aux échafaudages d'import ({@link BACKGROUND_ID_PREFIX}) : c'est lui
 * qui garantit qu'aucun chemin — y compris un geste de l'opérateur sur la carte — ne
 * l'enregistrera comme une Feature de la carte.
 *
 * Le nom de calque est encodé : un pseudo-calque s'appelle `fill rgb(3%, 36%, 58%) · stroke …`,
 * avec des espaces, des virgules et des points médians.
 */
export function planShapeShapeId(importId: number, layer: string, index: number): string {
	return `${BACKGROUND_ID_PREFIX}${importId}-shape-${index}-${encodeURIComponent(layer)}`;
}

/**
 * Point de l'aperçu (pixels) -> EPSG:3857, avec le placement du fond.
 *
 * MÊME affine que les 4 coins du fond (`buildBackgroundFeature`) : `u` est l'axe horizontal de
 * l'image, `v` son axe vertical — dirigé vers le sud à rotation nulle, le `y` d'une image
 * descendant là où celui du 3857 monte. Une forme posée autrement paraîtrait juste « un peu
 * décalée », ce qui est le pire des cas : ça ne se voit qu'une fois les POI générés.
 */
export function shapePixelToMercator(
	point: [number, number],
	params: { placement: DxfPlacement; widthPx: number; heightPx: number },
): [number, number] {
	const { placement, widthPx, heightPx } = params;
	const cos = Math.cos(placement.rotation);
	const sin = Math.sin(placement.rotation);

	const offsetX = (point[0] - widthPx / 2) * placement.scale;
	const offsetY = (point[1] - heightPx / 2) * placement.scale;

	return [
		placement.center[0] + offsetX * cos + offsetY * sin,
		placement.center[1] + offsetX * sin - offsetY * cos,
	];
}

/**
 * Feature d'éditeur (format v2, coordonnées 3857) d'une forme de l'aperçu.
 *
 * **Verrouillée** (`extra.lock`) : l'adapter n'émet alors ni `shape:click`, ni `shape:dblclick`,
 * ni menu contextuel dessus. C'est exactement ce qu'on veut d'un échafaudage — pas de sélection
 * qui remplacerait le panneau d'import par les « réglages de forme », pas d'édition, pas de
 * déplacement accidentel d'un dessin qui ne sera jamais enregistré.
 */
export function buildPlanShapeFeature(params: {
	shapeId: string;
	shape: PlanShape;
	placement: DxfPlacement;
	widthPx: number;
	heightPx: number;
	enabled: boolean;
	/** Forme signalée par la file des doutes : peinte en ambre, quoi qu'il en soit par ailleurs. */
	doubtful?: boolean;
}): LatestRawFeature {
	const { shapeId, shape, placement, widthPx, heightPx, enabled, doubtful = false } = params;

	const ring = shape.points.map(point => shapePixelToMercator(point, { placement, widthPx, heightPx }));
	// Anneau fermé : le format attend le premier point répété en dernier.
	const closed = [...ring, ring[0]!];

	return {
		type: 'Feature',
		geometry: {
			type: 'Polygon',
			coordinates: [closed],
			properties: {
				id: shapeId,
				extra: {
					// L'aperçu ne doit pas bloquer le calcul d'itinéraire le temps qu'il est posé.
					walkable: true,
					// Voir la note de verrouillage ci-dessus : l'aperçu se regarde.
					lock: true,
					style: planShapeStyle(shape, enabled, doubtful),
				},
			},
		},
	};
}

/**
 * Style d'une forme, dicté par son SEUL état — jamais par les couleurs du document.
 *
 * Le REMPLISSAGE est translucide, le trait plein. L'adapter peint les polygones à
 * `fill-opacity: 1` : un aplat opaque cacherait le plan posé dessous, c'est-à-dire tout ce qui
 * permet de reconnaître une forme — allées, logos, numéros. L'alpha passe donc par la couleur
 * elle-même (`rgba(...)`, que MapLibre lit comme n'importe quelle couleur CSS).
 *
 * Les quatre styles se distinguent sur DEUX axes, pas un seul : la teinte, et la facture du
 * trait (plein/pointillé, avec ou sans remplissage). C'est ce qui les garde lisibles pour un
 * daltonien comme sur un plan déjà chargé.
 */
export function planShapeStyle(shape: PlanShape, enabled: boolean, doubtful = false): PlanShapeStyle {
	return PLAN_SHAPE_STYLES[planShapeState({ shape, enabled, doubtful })];
}

/** Le style de chaque état, la seule table qui décide de ce qu'on voit. */
export const PLAN_SHAPE_STYLES: Record<PlanShapeState, PlanShapeStyle> = {
	// Plein et épais : une forme à regarder doit se voir de loin, au milieu de mille.
	doubtful: {
		bgColor: withAlpha(DOUBTFUL_COLOUR, 0.45),
		borderColor: DOUBTFUL_COLOUR,
		borderWidth: 3,
		borderDashed: false,
	},

	enabled: {
		bgColor: withAlpha(ENABLED_COLOUR, 0.35),
		borderColor: ENABLED_COLOUR,
		borderWidth: 2,
		borderDashed: false,
	},

	// Gris plein, trait continu : une décision prise, qui ne demande plus rien.
	discarded: {
		bgColor: withAlpha(DISABLED_COLOUR, 0.35),
		borderColor: DISABLED_COLOUR,
		borderWidth: 1,
		borderDashed: false,
	},

	// Pointillé SANS remplissage : présente à l'écran, absente de l'import. C'est exactement ce
	// qu'elle est — et ce qui la sépare d'un coup d'œil du gris plein de l'écarté.
	unsupported: {
		bgColor: 'transparent',
		borderColor: UNSUPPORTED_COLOUR,
		borderWidth: 1.5,
		borderDashed: true,
	},
};

/**
 * La LÉGENDE de l'écran de tri, dans l'ordre où elle se lit : ce qui sera créé d'abord, ce qui
 * réclame une décision ensuite, ce qui tombe à la fin.
 *
 * Elle sort de la même table que les formes ({@link PLAN_SHAPE_STYLES}) : une légende recopiée
 * à la main ment dès la première retouche de palette, et une légende qui ment est pire que pas
 * de légende du tout.
 */
export const PLAN_SHAPE_LEGEND: { state: PlanShapeState; label: string; hint: string }[] = [
	{ state: 'enabled', label: 'Sera importée', hint: 'créée sur la carte en l’état' },
	{ state: 'doubtful', label: 'À vérifier', hint: 'signalée : décidez-en dans la liste de droite' },
	{ state: 'discarded', label: 'Écartée', hint: 'vous l’avez retirée de l’import' },
	{ state: 'unsupported', label: 'Non reconnue', hint: 'forme trop complexe : cliquez pour la forcer' },
];

/**
 * Couleur CSS translucide depuis un hexadécimal (`#rgb` ou `#rrggbb`).
 *
 * Une valeur qu'on ne sait pas lire est rendue TELLE QUELLE : mieux vaut une forme opaque
 * qu'une forme perdue. Le cas ne se présente plus depuis que la palette est fixe, mais la
 * fonction reste défensive — elle est le seul point de passage des couleurs de l'écran.
 */
function withAlpha(colour: string, alpha: number): string {
	const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(colour)?.[1];
	if (!hex) return colour;

	const full = 3 === hex.length ? [...hex].map(c => c + c).join('') : hex;
	const [red, green, blue] = [0, 2, 4].map(offset => parseInt(full.slice(offset, offset + 2), 16));

	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/**
 * Catégorie qui RÉCLAME cette forme, ou `null` si aucune.
 *
 * ⚠️ Miroir exact de `PlanLayerMapping::getCategoryForShape()` côté serveur : une catégorie qui
 * désigne le rang gagne, à défaut la catégorie « tout le calque » le récupère. Diverger d'ici
 * ferait griser une forme qui s'importerait quand même — ou l'inverse.
 */
export function shapeOwner(categories: PlanCategory[], layer: string, index: number): PlanCategory | null {
	let wholeLayer: PlanCategory | null = null;

	for (const category of categories) {
		if (category.baseLayer !== layer) continue;

		if (null === category.shapes) {
			wholeLayer ??= category;
			continue;
		}

		if (category.shapes.includes(index)) return category;
	}

	return wholeLayer;
}

/**
 * Ids des formes de l'aperçu qui appartiennent à une catégorie — ce que le bouton œil surligne.
 *
 * L'appartenance est celle du serveur ({@see shapeOwner}), pas une comparaison de calques : deux
 * lignes peuvent partager le même `baseLayer` et ne se distinguer que par leurs rangs. Sur un plan
 * raster, « Forme 12 » n'en désigne qu'UNE, et c'est précisément celle qu'il faut montrer.
 */
export function shapeIdsOfCategory(params: {
	importId: number;
	shapes: PlanShape[];
	categories: PlanCategory[];
	category: PlanCategory;
}): string[] {
	const { importId, shapes, categories, category } = params;

	return shapes
		.filter(shape => shapeOwner(categories, shape.layer, shape.index)?.key === category.key)
		.map(shape => planShapeShapeId(importId, shape.layer, shape.index));
}

/**
 * **Couleur DOMINANTE des formes d'une catégorie** — la pastille de la liste des calques.
 *
 * La couleur du document ne peint plus les formes de l'écran de tri, qui parle d'états et non
 * de calques ({@link PLAN_SHAPE_STYLES}). Elle reste pourtant ce qui distingue un calque d'un
 * autre à l'œil sur le plan papier, et c'est là qu'elle sert : dans la liste, en face du nom de
 * la ligne, pour retrouver « le calque bleu » sans ouvrir l'aperçu.
 *
 * Dominante et non « celle de la première forme » : un calque de contours porte parfois une
 * forme repeinte à la main, et c'est la couleur de la MASSE qu'on reconnaît sur le plan.
 *
 * Rend `null` quand aucune forme de la catégorie ne dit sa couleur — ou qu'aucune n'a été
 * transmise (plan tronqué). L'appelant retombe alors sur {@link layerSwatch}, qui lit la
 * couleur inscrite dans le nom des pseudo-calques.
 */
export function categoryColour(
	shapes: PlanShape[],
	categories: PlanCategory[],
	category: PlanCategory,
): string | null {
	const tally = new Map<string, number>();

	for (const shape of shapes) {
		if (shapeOwner(categories, shape.layer, shape.index)?.key !== category.key) continue;

		const colour = shape.fillColor || shape.strokeColor;
		if (!colour) continue;

		tally.set(colour, (tally.get(colour) ?? 0) + 1);
	}

	// À égalité, la première rencontrée gagne : `Map` garde l'ordre d'insertion, donc celui des
	// formes, qui est celui du serveur — stable d'une analyse à l'autre.
	let best: { colour: string; count: number } | null = null;
	for (const [colour, count] of tally) {
		if (!best || count > best.count) best = { colour, count };
	}

	return best?.colour ?? null;
}

/** La forme sera-t-elle importée en l'état ? */
export function isShapeEnabled(categories: PlanCategory[], layer: string, index: number): boolean {
	return shapeOwner(categories, layer, index)?.enabled ?? false;
}
